/**
 * useNetworkStatus Hook
 *
 * React hook that provides network status monitoring.
 * Returns current online/offline state and queued action count.
 */

import { useState, useEffect, useCallback } from 'react';
import { networkStatusService, type NetworkStatus } from '../services/networkStatusService';
import { API_ORIGIN } from '../config/api';

export interface UseNetworkStatusResult {
  /** Current network status */
  status: NetworkStatus;
  /** Whether the app is currently online */
  isOnline: boolean;
  /** Number of queued actions waiting for network */
  queuedActionCount: number;
  /** Queue an action for when network is restored */
  queueAction: (action: () => Promise<unknown>, description: string) => string;
  /** Manually trigger a connectivity check */
  checkConnectivity: () => Promise<boolean>;
}

export function useNetworkStatus(): UseNetworkStatusResult {
  const [status, setStatus] = useState<NetworkStatus>(networkStatusService.getStatus());
  const [queuedActionCount, setQueuedActionCount] = useState(
    networkStatusService.getQueuedActions().length
  );

  useEffect(() => {
    // Same-origin /api in production when API_ORIGIN is empty
    const apiUrl = API_ORIGIN || '';
    networkStatusService.initialize({
      pingUrl: `${apiUrl}/api/health`,
    });

    // Subscribe to status changes
    const unsubscribe = networkStatusService.subscribe((newStatus) => {
      setStatus(newStatus);
      setQueuedActionCount(networkStatusService.getQueuedActions().length);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const queueAction = useCallback((action: () => Promise<unknown>, description: string) => {
    const id = networkStatusService.queueAction(action, description);
    setQueuedActionCount(networkStatusService.getQueuedActions().length);
    return id;
  }, []);

  const checkConnectivity = useCallback(() => {
    return networkStatusService.checkConnectivity();
  }, []);

  return {
    status,
    isOnline: status === 'online',
    queuedActionCount,
    queueAction,
    checkConnectivity,
  };
}
